import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { requestJson } from "../../helpers/api";

type Accent =
  | "primary"
  | "success"
  | "info"
  | "warning"
  | "danger"
  | "secondary"
  | "dark";

type SubmoduloResponse = {
  codigo: string;
  titulo: string;
  descripcion: string | null;
  ruta: string;
  icono: string | null;
  permiso: string | null;
  orden: number;
};

type ModuloResponse = {
  codigo: string;
  titulo: string;
  descripcion: string | null;
  icono: string | null;
  accent: string | null;
  orden: number;
  submodulos: SubmoduloResponse[];
  tags?: string[];
};

const ACCENT_DEFAULT: Accent = "primary";
const ACCENT_PERMITIDOS: Accent[] = ["primary", "success", "info", "warning", "danger", "secondary", "dark"];

const normalizarAccent = (valor?: string | null): Accent => {
  if (valor && ACCENT_PERMITIDOS.includes(valor as Accent)) {
    return valor as Accent;
  }
  return ACCENT_DEFAULT;
};

const MAX_VISIBLE_ACTIONS = 3;

const normalizarRuta = (ruta?: string | null): string => {
  if (!ruta) {
    return "#";
  }
  const base = ruta.startsWith("/") ? ruta : `/${ruta}`;
  return base.replace(/^\/inicio\//i, "/");
};

type ModuleCardProps = {
  card: ModuloResponse;
  onSeeAll?: (card: ModuloResponse) => void;
};

const ModuleCard: React.FC<ModuleCardProps> = ({ card, onSeeAll }) => {
  const accent = normalizarAccent(card.accent);
  const accionesMostradas = card.submodulos.slice(0, MAX_VISIBLE_ACTIONS);
  const hayMas = card.submodulos.length > MAX_VISIBLE_ACTIONS;
  return (
    <div className="module-card h-100 d-flex flex-column gap-2">
      <Card className="flex-grow-1 border-0 shadow-sm">
        <CardBody className="d-flex flex-column gap-2 p-2 pe-3 pt-4">
          <div>
            <h6 className="mb-1">{card.titulo}</h6>
            <p className="text-muted small mb-0">{card.descripcion}</p>
          </div>
          {card.tags ? (
            <div className="d-flex flex-wrap gap-1">
              {card.tags.map((tag) => (
                <Badge
                  key={`${card.codigo}-${tag}`}
                  color=""
                  className="bg-secondary-subtle text-secondary-emphasis"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          ) : null}
        </CardBody>
      </Card>
      <div className="d-flex flex-column gap-2">
        {accionesMostradas.map((action) => (
          <Link
            key={`${card.codigo}-${action.codigo}`}
            to={{ pathname: normalizarRuta(action.ruta) }}
            className={`btn btn-soft-${accent} btn-sm w-100 d-flex align-items-center justify-content-between`}
          >
            <span>{action.titulo}</span>
            <i className="ri-arrow-right-up-line ms-2" />
          </Link>
        ))}
        {hayMas ? (
          <button
            type="button"
            className={`btn btn-outline-${accent} btn-sm`}
            onClick={() => onSeeAll?.(card)}
          >
            Ver todos ({card.submodulos.length})
          </button>
        ) : null}
      </div>
    </div>
  );
};

const InicioPage = () => {
  const [modulos, setModulos] = useState<ModuloResponse[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moduloSeleccionado, setModuloSeleccionado] = useState<ModuloResponse | null>(null);
  const [panelAbierto, setPanelAbierto] = useState(false);

  useEffect(() => {
    document.title = "Inicio | Saas";
  }, []);

  useEffect(() => {
    let activo = true;
    const cargar = async () => {
      setCargando(true);
      setError(null);
      try {
        const respuesta = await requestJson<ModuloResponse[]>("/api/modulos");
        if (!activo) return;
        setModulos(Array.isArray(respuesta) ? respuesta : []);
      } catch (err) {
        console.error(err);
        if (!activo) return;
        setError("No pudimos obtener tus módulos habilitados. Intentá nuevamente.");
        setModulos([]);
      } finally {
        if (activo) {
          setCargando(false);
        }
      }
    };
    void cargar();
    return () => {
      activo = false;
    };
  }, []);

  const tarjetasFiltradas = modulos.filter(
    (modulo) => Array.isArray(modulo.submodulos) && modulo.submodulos.length > 0,
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Inicio" pageTitle="Panel principal" />

        {error ? <Alert color="danger">{error}</Alert> : null}

        {cargando ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : tarjetasFiltradas.length === 0 ? (
          <div className="text-center text-muted py-5">
            {modulos.length === 0
              ? "No tenés módulos habilitados todavía. Consultá con un administrador."
              : "No encontramos módulos que coincidan con tu búsqueda."}
          </div>
        ) : (
          <Row className="g-2 g-md-3 row-cols-lg-7 row-cols-md-3 row-cols-2">
            {tarjetasFiltradas.map((card) => (
              <Col key={card.codigo}>
                <ModuleCard
                  card={card}
                  onSeeAll={(modulo) => {
                    setModuloSeleccionado(modulo);
                    setPanelAbierto(true);
                  }}
                />
              </Col>
            ))}
          </Row>
        )}

        <Offcanvas direction="end" isOpen={panelAbierto} toggle={() => setPanelAbierto(false)} scrollable>
          <OffcanvasHeader toggle={() => setPanelAbierto(false)}>
            {moduloSeleccionado?.titulo ?? "Accesos del módulo"}
          </OffcanvasHeader>
          <OffcanvasBody>
            {moduloSeleccionado ? (
              <div className="d-flex flex-column gap-2">
                <p className="text-muted small">{moduloSeleccionado.descripcion}</p>
                {moduloSeleccionado.submodulos.map((action) => (
                  <Link
                    key={`${moduloSeleccionado.codigo}-offcanvas-${action.codigo}`}
                    to={{ pathname: normalizarRuta(action.ruta) }}
                    className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-between"
                    onClick={() => setPanelAbierto(false)}
                  >
                    <span>{action.titulo}</span>
                    <i className="ri-arrow-right-up-line ms-2" />
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-muted mb-0">Seleccioná un módulo para ver todas sus acciones.</p>
            )}
          </OffcanvasBody>
        </Offcanvas>
      </Container>
    </div>
  );
};

export default InicioPage;
